import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedResponse } from '../interfaces/api.interface';

// DTOs
export interface SubscriptionFilters {
  status?: 'active' | 'inactive' | 'trial' | 'expired' | 'suspended' | 'cancelled';
  planId?: number;
  companyId?: number;
  page?: number;
  limit?: number;
}

export interface ExtendSubscriptionDto {
  days: number;
  reason: string;
}

export interface AddPaymentDto {
  amount: number;
  paymentMethod: 'stripe' | 'bank_transfer' | 'cash';
  paymentReference?: string;
  notes?: string;
}

export interface SuspendSubscriptionDto {
  reason: string;
  suspendUntil?: string; // ISO date string
}

export interface ResumeSubscriptionDto {
  reason: string;
}

// Response DTOs
export interface SubscriptionListItem {
  id: number;
  companyId: number;
  companyName: string;
  planId: number;
  planName: string;
  status: string;
  startDate: string;
  endDate: string;
  nextPaymentDate: string | null;
  autoRenew: boolean;
}

export interface PaymentHistoryItem {
  id: number;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  status: string;
  paymentReference: string | null;
  failureReason: string | null;
  processedBy: number | null;
  notes: string | null;
}

export interface SubscriptionDetail {
  id: number;
  companyId: number;
  companyName: string;
  companyEmail: string;
  companyPhone: string | null;
  planId: number;
  planName: string;
  planDescription: string;
  status: string;
  startDate: string;
  endDate: string;
  nextPaymentDate: string | null;
  autoRenew: boolean;
  suspendedAt: string | null;
  suspendedReason: string | null;
  payments: PaymentHistoryItem[];
}

@Injectable({
  providedIn: 'root'
})
export class CompanySubscriptionsService {
  private readonly API_URL = environment.apiUrl || 'http://localhost:3333';
  private readonly SUBSCRIPTIONS_ENDPOINT = `${this.API_URL}/admin/company-subscriptions`;

  constructor(private http: HttpClient) {}

  /**
   * Get paginated list of company subscriptions with filters
   */
  getSubscriptionsList(filters?: SubscriptionFilters): Observable<PaginatedResponse<SubscriptionListItem>> {
    let params = new HttpParams();
    
    if (filters) {
      if (filters.status) {
        params = params.set('status', filters.status);
      }
      if (filters.planId) {
        params = params.set('planId', filters.planId.toString());
      }
      if (filters.companyId) {
        params = params.set('companyId', filters.companyId.toString());
      }
      if (filters.page) {
        params = params.set('page', filters.page.toString());
      }
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      }
    }

    return this.http.get<PaginatedResponse<SubscriptionListItem>>(
      this.SUBSCRIPTIONS_ENDPOINT,
      { params }
    );
  }

  /**
   * Get subscription detail with payment history
   */
  getSubscriptionDetail(id: number): Observable<ApiResponse<SubscriptionDetail>> {
    return this.http.get<ApiResponse<SubscriptionDetail>>(
      `${this.SUBSCRIPTIONS_ENDPOINT}/${id}`
    );
  }

  /**
   * Extend subscription without payment (manual extension)
   */
  extendSubscription(id: number, dto: ExtendSubscriptionDto): Observable<ApiResponse<SubscriptionDetail>> {
    return this.http.put<ApiResponse<SubscriptionDetail>>(
      `${this.SUBSCRIPTIONS_ENDPOINT}/${id}/extend`,
      dto
    );
  }

  /**
   * Add manual payment (bank transfer, cash, etc.)
   */
  addManualPayment(id: number, dto: AddPaymentDto): Observable<ApiResponse<PaymentHistoryItem>> {
    return this.http.post<ApiResponse<PaymentHistoryItem>>(
      `${this.SUBSCRIPTIONS_ENDPOINT}/${id}/payments`,
      dto
    );
  }

  /**
   * Suspend subscription with reason
   */
  suspendSubscription(id: number, dto: SuspendSubscriptionDto): Observable<ApiResponse<SubscriptionDetail>> {
    return this.http.put<ApiResponse<SubscriptionDetail>>(
      `${this.SUBSCRIPTIONS_ENDPOINT}/${id}/suspend`,
      dto
    );
  }

  /**
   * Resume suspended subscription
   */
  resumeSubscription(id: number, dto: ResumeSubscriptionDto): Observable<ApiResponse<SubscriptionDetail>> {
    return this.http.put<ApiResponse<SubscriptionDetail>>(
      `${this.SUBSCRIPTIONS_ENDPOINT}/${id}/resume`,
      dto
    );
  }
}
